import type { ReactNode } from 'react';
import { ShieldIcon } from '../icons';
import { supportedFormatList } from '../../lib/formats/capabilities';
import { cn } from '../../lib/utils/cn';

export type View = 'home' | 'image' | 'text';

export function AppShell({
  view,
  onNavigate,
  children,
}: {
  view: View;
  onNavigate: (view: View) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--surface)]/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => onNavigate('home')}
            className="flex items-center gap-2.5"
            aria-label="CleanFile home"
          >
            <span className="flex size-8 items-center justify-center rounded-lg bg-brand-600 text-white">
              <ShieldIcon width={18} height={18} />
            </span>
            <span className="text-strong text-[15px] font-semibold tracking-tight">CleanFile</span>
          </button>

          <nav className="flex items-center gap-1" aria-label="Main">
            {(
              [
                { id: 'image', label: 'Inspect a file' },
                { id: 'text', label: 'Clean text' },
              ] as const
            ).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                aria-current={view === item.id ? 'page' : undefined}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  view === item.id
                    ? 'bg-[var(--surface-sunken)] text-strong'
                    : 'text-muted hover:text-strong',
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10">{children}</main>

      <footer className="border-t border-[var(--border)] px-4 py-6 sm:px-6">
        <div className="text-muted mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 text-xs">
          <p>
            CleanFile processes files in your browser. Nothing you open here is uploaded, stored or sent
            anywhere.
          </p>
          <p>{supportedFormatList()} · plain text</p>
        </div>
      </footer>
    </div>
  );
}

/**
 * The privacy promise, shown under every upload area.
 *
 * Each of these three is a statement about the code rather than a slogan. There is no upload path in
 * the app at all; there is no account system to sign in to; and the Content-Security-Policy served
 * with the page sets connect-src to 'none', which means the browser itself refuses to let this page
 * make a network request. A test asserts that no request leaves the page while a file is cleaned.
 */
export function PrivacyPoints({ className }: { className?: string }) {
  const points = ['No file upload', 'No account required', 'No server processing'];
  return (
    <ul className={cn('flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5', className)}>
      {points.map((point) => (
        <li key={point} className="text-muted flex items-center gap-1.5 text-[13px]">
          <span aria-hidden className="size-1 rounded-full bg-brand-500" />
          {point}
        </li>
      ))}
    </ul>
  );
}
